import {
  Box,
  Chip,
  Grid,
  Paper,
  Button,
  Typography,
  TextField,
  FormControl,
  Select,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { Search as SearchIcon, FilterAltOutlined as FilterAltOutlinedIcon, MoreVert as MoreVertIcon, RefreshOutlined as RefreshOutlinedIcon } from "../../../../../components/icons";
import draftCase from "../../../../../assets/images/draftCase.svg";
import activeCase from "../../../../../assets/images/activeCase.svg";
import completedCase from "../../../../../assets/images/completedCase.svg";
import draftQuot from "../../../../../assets/images/draftQuot.svg";
import offeredQuot from "../../../../../assets/images/offeredQuot.svg";
import acceptedQuot from "../../../../../assets/images/acceptedQuot.svg";
import rejectIcon from "../../../../../assets/images/rejectIcon.svg";
import blueCheck from "../../../../../assets/images/blueCheck.svg";
import loader from "../../../../../assets/images/loader.svg";
import stakeholder from "../../../../../assets/images/stakeholder.svg";
import ask from "../../../../../assets/images/ask.svg";
import { useEffect, useState } from "react";
import { useCaseStore } from "../../../../../store/useCaseStore";
import { formatR } from "../../../../../utils/money";
import Loader from "../../../../../components/Loader";
import dayjs from "dayjs";
import { useBodyRemovalsStore } from "../../../../../store/useBodyRemovalsStore";
import { useQuotationStore } from "../../../../../store/useQuotationStore";
import {
  getBodyRemovalHistory,
  getCaseDetails,
  getCaseHistory,
} from "../../../../../services/dashboard";
import { useToast } from "../../../../../hooks/useToast";
import History from "../../../claims/common/History";
import { useAuthStore } from "../../../../../store/useAuthStore";
import {
  OperationsPageShell,
  OperationsSectionHeader,
  formatOperationsStatus,
  ifasColors,
  operationsTablePaperSx,
} from "../../../ifas/components/OperationsUI";
import {
  StandardMetricCard,
  StatusDotChip,
  StandardCountBadge,
  StandardPager,
  STANDARD_TONES,
  standardTableHeadCellSx,
  standardTableRowSx,
  standardListScrollSx,
  standardGhostBtnSx,
  standardOutlineBtnSx,
  standardStatusTone,
  type Tone,
} from "../../../ifas/components/StandardUI";
import { useUserStore } from "../../../../../store/useUserStore";
import { hasIfasCapability } from "../../../../../utils/ifasAccess";
import { useOperationalBranchNames } from "../../../../../hooks/useOperationalBranchNames";
import { AssignPlannerDialog, PlannerBranchSetupDialog } from "./PlannerAssignmentDialogs";
import OperationalLocationsDialog from "./OperationalLocationsDialog";

const getBodyRemovalStatusStyles = (status: string) => {
  switch (status) {
    case "DRAFT":
      return {
        backgroundColor: "#FFF3E0",
        color: "#EF6C00",
      };
    case "ALLOCATED":
      return {
        backgroundColor: "#E8F5E9",
        color: "#398916",
      };
    case "DRIVER_ACCEPTED":
      return {
        backgroundColor: "#EAF1FF",
        color: "#1B56EB",
      };
    case "EN_ROUTE":
      return {
        backgroundColor: "#EAF1FF",
        color: "#1B56EB",
      };
    case "ARRIVED_AT_SCENE":
      return {
        backgroundColor: "#FFF4EB",
        color: "#EF6C00",
      };
    case "DETAILS_CONFIRMED":
      return {
        backgroundColor: "#E8F5E9",
        color: "#2E7D32",
      };
    case "REMOVAL_ORDER_CREATED":
      return {
        backgroundColor: "#E3F2FD",
        color: "#1565C0",
      };
    case "BODY_COLLECTED":
      return {
        backgroundColor: "rgba(27, 86, 235, 0.2)",
        color: "#1B56EB",
      };
    case "ARRIVED_AT_MORTUARY":
      return {
        backgroundColor: "#E8F5E9",
        color: "#2E7D32",
      };
    case "BODY_DELIVERED":
      return {
        backgroundColor: "rgba(27, 180, 235, 0.18)",
        color: "#1BB4EB",
      };
    default:
      return {
        backgroundColor: "#EEEEEE",
        color: "#616161",
      };
  }
};

const getBodyRemovalStatusLabel = (status: string) => {
  switch (status) {
    case "BODY_COLLECTED":
      return "collected";
    case "BODY_DELIVERED":
      return "delivered";
    case "DRIVER_ACCEPTED":
      return "dispatch accepted";
    case "EN_ROUTE":
      return "en route";
    case "ARRIVED_AT_SCENE":
      return "arrived at scene";
    case "ARRIVED_AT_MORTUARY":
      return "arrived at mortuary";
    case "DETAILS_CONFIRMED":
      return "details confirmed";
    case "REMOVAL_ORDER_CREATED":
      return "order created";
    default:
      return status?.toLowerCase() || "n/a";
  }
};

const isBodyRemovalStatusPill = (status: string) =>
  [
    "BODY_COLLECTED",
    "BODY_DELIVERED",
    "DETAILS_CONFIRMED",
    "REMOVAL_ORDER_CREATED",
    "DRAFT",
    "ALLOCATED",
    "DRIVER_ACCEPTED",
    "EN_ROUTE",
    "ARRIVED_AT_SCENE",
    "ARRIVED_AT_MORTUARY",
  ].includes(status);

// Quotation states only, per Tony's ruling: DRAFT / OFFERED / ACCEPTED / REJECTED.
// Never a downstream catering/tombstone status.
const QUOTATION_STATUS_TONE: Record<string, Tone> = {
  DRAFT: "amber",
  OFFERED: "purple",
  ACCEPTED: "green",
  REJECTED: "red",
};
const quotationStatusTone = (status?: string): Tone =>
  QUOTATION_STATUS_TONE[String(status || "").toUpperCase()] || "grey";
const QUOTATION_STATUS_OPTIONS = Object.keys(QUOTATION_STATUS_TONE);

const CasePortal = ({
  title,
  fetchCases,
  fetchBodyRemovals,
  fetchQuotations,
  onSelectCase,
  onSelectBodyRemoval,
  onSelectQuotation,
  recentFilter,
  currentPage,
  rowsPerPage,
  setCurrentPage,
  setClaimsTotalCount,
  showDateOfDeath,
  isBodyRemoval = false,
  isQuotation = false,
  driverOnly = false,
  isIfasCasePortal = false,
  applyIfasCaseFilters,
  selectedIfasStatus,
  selectedIfasSearchText,
  isIfasBodyRemovalPortal = false,
  applyIfasBodyRemovalFilters,
  selectedIfasBodyRemovalStatus,
  selectedIfasBodyRemovalSearchText,
}: any) => {
  const [isLoading, setIsLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedActionItem, setSelectedActionItem] = useState<any>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState<any>(null);
  const [filterSearch, setFilterSearch] = useState(selectedIfasSearchText || "");
  const [filterStatus, setFilterStatus] = useState<string>(selectedIfasStatus || "");
  const [filterBranch, setFilterBranch] = useState("");
  const [bodyFilterSearch, setBodyFilterSearch] = useState(selectedIfasBodyRemovalSearchText || "");
  const [bodyFilterStatus, setBodyFilterStatus] = useState<string>(selectedIfasBodyRemovalStatus || "");
  const [quotationFilterSearch, setQuotationFilterSearch] = useState("");
  const [quotationFilterStatus, setQuotationFilterStatus] = useState("");
  const [showPlannerSetup, setShowPlannerSetup] = useState(false);
  const [showLocationSetup, setShowLocationSetup] = useState(false);
  const [showAssignPlanner, setShowAssignPlanner] = useState(false);

  const { setSelectedCase } = useCaseStore();
  const { showInfoToast } = useToast();
  const { userId, driverId, email, first_name, last_name, user_name } = useAuthStore();
  const role = useUserStore((state) => state.role);
  const { branchOptions, fetchBranchNames } = useOperationalBranchNames("MORTUARY");
  const canAssignSomeone = hasIfasCapability(role, "caseAssignSomeone");
  const canManagePlannerBranches = hasIfasCapability(role, "plannerBranchManage");
  const canManageOperationalLocations = hasIfasCapability(role, "operationalLocationManage");

  const {
    cases,
    totalCount: caseTotal,
    statusCounts: caseStatusCounts,
  } = useCaseStore();

  const {
    bodyRemovals,
    totalElements: bodyTotal,
    statusCounts: bodyStatusCounts,
  } = useBodyRemovalsStore();

  const {
    quotations,
    totalElements: quotationTotal,
    statusCounts: quotationStatusCounts,
  } = useQuotationStore();

  const paginatedData = isBodyRemoval
    ? bodyRemovals
    : isQuotation
      ? quotations
      : cases;

  const totalElements = isBodyRemoval
    ? bodyTotal
    : isQuotation
      ? quotationTotal
      : caseTotal;
  const statusCounts = isBodyRemoval
    ? bodyStatusCounts
    : isQuotation
      ? quotationStatusCounts
      : caseStatusCounts;

  const normalizeMatchValue = (value: any) =>
    String(value || "").trim().toLowerCase();

  const driverKeys = [
    driverId,
    userId,
    email,
    user_name,
    `${first_name || ""} ${last_name || ""}`.trim(),
  ]
    .map(normalizeMatchValue)
    .filter(Boolean);

  const isAssignedToCurrentDriver = (item: any) => {
    if (!driverKeys.length) return false;

    const normalizedAuthDriverId = normalizeMatchValue(driverId);
    const normalizedAssignedDriverId = normalizeMatchValue(
      item?.driverId ||
        item?.driverID ||
        item?.allocationDetails?.driverId ||
        item?.allocationDetails?.driverID,
    );

    if (normalizedAuthDriverId && normalizedAssignedDriverId) {
      return normalizedAuthDriverId === normalizedAssignedDriverId;
    }

    const candidates = [
      item?.driverId,
      item?.driverID,
      item?.assignedDriverId,
      item?.assignedDriverID,
      item?.driverUserId,
      item?.driverEmail,
      item?.assignedDriverEmail,
      item?.driverName,
      item?.assignedDriverName,
      item?.allocationDetails?.driverId,
      item?.allocationDetails?.driverID,
      item?.allocationDetails?.driverUserId,
      item?.allocationDetails?.driverEmail,
      item?.allocationDetails?.driverName,
      item?.allocationDetails?.driverPhone,
    ]
      .map(normalizeMatchValue)
      .filter(Boolean);

    return candidates.some((candidate) => driverKeys.includes(candidate));
  };

  const baseData = paginatedData as any[];

  const driverFilteredData =
    driverOnly && isBodyRemoval
      ? baseData.filter(isAssignedToCurrentDriver)
      : baseData;

  // Quotations have no dedicated search endpoint today, so this narrows the
  // already-fetched page client-side; it does not change what is fetched.
  const visibleData =
    isQuotation && (quotationFilterSearch || quotationFilterStatus)
      ? driverFilteredData.filter((item: any) => {
          const matchesStatus = quotationFilterStatus
            ? String(item.quotationStatus || "").toUpperCase() === quotationFilterStatus
            : true;
          const needle = quotationFilterSearch.trim().toLowerCase();
          const matchesSearch = needle
            ? [
                item.quotationNumber,
                item.caseNumber,
                item.claimantFirstName,
                item.claimantLastName,
                item.deceasedFirstName,
                item.deceasedLastName,
              ]
                .filter(Boolean)
                .some((field: string) => String(field).toLowerCase().includes(needle))
            : true;
          return matchesStatus && matchesSearch;
        })
      : driverFilteredData;
  const effectiveTotalElements =
    driverOnly && isBodyRemoval ? visibleData.length : totalElements;

  const visibleBodyStatusCounts =
    driverOnly && isBodyRemoval
      ? visibleData.reduce((counts: Record<string, number>, item: any) => {
          const status = String(item?.bodyRemovalStatus || item?.status || "");
          if (status) counts[status] = (counts[status] || 0) + 1;
          return counts;
        }, {})
      : null;

  const metricStatusCounts = visibleBodyStatusCounts || statusCounts;

  // Ref/name/deceased (3) + Amount (quotation) + Date of Death/Last Updated (case) +
  // Assigned (case/body removal) + Status + Actions/Open.
  const emptyStateColSpan =
    3 +
    (isQuotation ? 1 : 0) +
    (showDateOfDeath ? 3 : 0) +
    (!isQuotation ? 1 : 0) +
    1 +
    1;

  const casePortalMetrics = [
    {
      label: "Total Draft Cases",
      value: metricStatusCounts?.DRAFT || 0,
      tone: "amber" as Tone,
      icon: draftCase,
    },
    {
      label: "Total Active Cases",
      value: metricStatusCounts?.IN_PROGRESS || 0,
      tone: "blue" as Tone,
      icon: activeCase,
    },
    {
      label: "Total Completed Cases",
      value: metricStatusCounts?.COMPLETED || 0,
      tone: "green" as Tone,
      icon: completedCase,
    },
  ];

  const bodyRemovalMetrics = [
    {
      label: "Draft",
      value: metricStatusCounts?.DRAFT || 0,
      tone: "amber" as Tone,
      icon: stakeholder,
    },
    {
      label: "Allocated",
      value: metricStatusCounts?.ALLOCATED || 0,
      tone: "purple" as Tone,
      icon: ask,
    },
    {
      label: "In Progress",
      value:
        (metricStatusCounts?.DRIVER_ACCEPTED || 0) +
        (metricStatusCounts?.EN_ROUTE || 0) +
        (metricStatusCounts?.ARRIVED_AT_SCENE || 0) +
        (metricStatusCounts?.REMOVAL_ORDER_CREATED || 0) +
        (metricStatusCounts?.BODY_COLLECTED || 0) +
        (metricStatusCounts?.ARRIVED_AT_MORTUARY || 0),
      tone: "blue" as Tone,
      icon: loader,
    },
    {
      label: "Delivered",
      value: metricStatusCounts?.BODY_DELIVERED || 0,
      tone: "green" as Tone,
      icon: blueCheck,
    },
  ];

  const quotationMetrics = [
    {
      label: "Draft Quotations",
      value: metricStatusCounts?.DRAFT || 0,
      tone: "amber" as Tone,
      caption: "Being prepared",
      icon: draftQuot,
    },
    {
      label: "Offered Quotations",
      value: metricStatusCounts?.OFFERED || 0,
      tone: "purple" as Tone,
      caption: "Sent to family",
      icon: offeredQuot,
    },
    {
      label: "Accepted Quotations",
      value: metricStatusCounts?.ACCEPTED || 0,
      tone: "green" as Tone,
      caption: "Ready to proceed",
      icon: acceptedQuot,
    },
    {
      label: "Rejected Quotations",
      value: metricStatusCounts?.REJECTED || 0,
      tone: "red" as Tone,
      caption: "Declined by family",
      icon: rejectIcon,
    },
  ];

  const metrics = isBodyRemoval
    ? bodyRemovalMetrics
    : isQuotation
      ? quotationMetrics
      : casePortalMetrics;
  const pageDescription = isBodyRemoval
    ? driverOnly
      ? "See the body removal work assigned to you, confirm collection, and complete delivery handover with clear operational steps."
      : "Track body removal requests from allocation through collection and delivery, with status and history close at hand."
    : isQuotation
      ? "Review quotation requests, pricing decisions, and accepted service options before the family moves forward."
      : "Manage funeral administration cases from draft through active handling and completion.";
  const pageEyebrow = isBodyRemoval
    ? "Funeral Administration"
    : isQuotation
      ? "Service Pricing"
      : "Case Management";

  const fetchAllCases = async (currentPage: any, rowsPerPage: any) => {
    setIsLoading(true);
    const response = await fetchCases(currentPage, rowsPerPage);

    if (response?.status === 200) {
      useCaseStore.getState().setCases(response.data);
      setClaimsTotalCount(response.data.totalCount);
    }
    setIsLoading(false);
  };

  const fetchAllBodyRemovals = async (page = 0, size = 10) => {
    setIsLoading(true);
    const response = await fetchBodyRemovals(driverOnly ? 0 : page, driverOnly ? 1000 : size);

    if (response?.status === 200) {
      useBodyRemovalsStore.getState().setBodyRemovals(response.data);
    }

    setIsLoading(false);
  };

  const fetchAllQuotations = async (page = 0, size = 10) => {
    setIsLoading(true);
    const response = await fetchQuotations(page, size);

    if (response?.status === 200) {
      useQuotationStore.getState().setQuotations(response.data);
    }

    setIsLoading(false);
  };

  // Re-runs whichever real loader this mode uses. No new data source.
  const reloadList = () => {
    // IFAS Case Portal list uses the dedicated search endpoint for pagination/filter/search.
    if (isIfasCasePortal && applyIfasCaseFilters && !isBodyRemoval && !isQuotation) {
      applyIfasCaseFilters({
        status: filterStatus || null,
        searchText: filterSearch,
        branchName: filterBranch || null,
        page: currentPage,
      });
      return;
    }

    // IFAS Body Removal list uses the dedicated search endpoint for pagination/filter/search.
    if (isIfasBodyRemovalPortal && applyIfasBodyRemovalFilters && isBodyRemoval && !isQuotation) {
      applyIfasBodyRemovalFilters({
        status: bodyFilterStatus || null,
        searchText: bodyFilterSearch,
        page: currentPage,
      });
      return;
    }

    if (isBodyRemoval && fetchBodyRemovals) {
      fetchAllBodyRemovals(currentPage, rowsPerPage);
    } else if (!isBodyRemoval && fetchCases) {
      fetchAllCases(currentPage, rowsPerPage);
    } else {
      fetchAllQuotations(currentPage, rowsPerPage);
    }
  };

  useEffect(() => {
    reloadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBodyRemoval, currentPage, rowsPerPage]);

  const handleHistoryClick = async () => {
    if (!selectedActionItem || isQuotation) {
      return;
    }

    setAnchorEl(null);
    setIsLoading(true);

    if (isBodyRemoval) {
      useBodyRemovalsStore.getState().setSelectedBodyRemoval(selectedActionItem);
      const bodyRemovalId =
        selectedActionItem.id || selectedActionItem.caseId || "";
      const res = await getBodyRemovalHistory(bodyRemovalId);

      if (res?.status === 200) {
        setHistoryData(res.data);
        setShowHistory(true);
      } else {
        showInfoToast("Failed to load body removal history. Please try again.");
      }

      setIsLoading(false);
      return;
    }

    setSelectedCase(selectedActionItem);
    const res = await getCaseHistory(selectedActionItem.caseID);
    if (res?.status === 200) {
      setHistoryData(res.data);
      setShowHistory(true);
    } else {
      showInfoToast("Failed to load case history. Please try again.");
    }
    setIsLoading(false);
  };

  const handleAssignSomeoneClick = () => {
    if (!selectedActionItem) return;
    setAnchorEl(null);
    setShowAssignPlanner(true);
  };

  const handleBackFromHistory = () => {
    setShowHistory(false);
  };

  const handleOpenDetails = async (item: any) => {
    if (isBodyRemoval) {
      useBodyRemovalsStore.getState().setSelectedBodyRemoval(item);
      onSelectBodyRemoval(item, item.bodyRemovalNumber);
      return;
    }

    if (isQuotation) {
      useQuotationStore.getState().setSelectedQuotation(item);
      onSelectQuotation(item, item.quotationNumber);
      return;
    }

    setIsLoading(true);
    try {
      const detailedCase = item?.caseNumber ? await getCaseDetails(item.caseNumber) : null;
      if (detailedCase?.status === 200 && detailedCase.data) {
        useCaseStore.getState().setSelectedCase(detailedCase.data);
        onSelectCase(detailedCase.data);
        return;
      }
    } catch (error) {
      console.error("Failed to load case details", error);
    } finally {
      setIsLoading(false);
    }

    useCaseStore.getState().setSelectedCase(item);
    onSelectCase(item);
  };

  useEffect(() => {
    setFilterSearch(selectedIfasSearchText || "");
  }, [selectedIfasSearchText]);

  useEffect(() => {
    setFilterStatus(selectedIfasStatus || "");
  }, [selectedIfasStatus]);

  useEffect(() => {
    setBodyFilterSearch(selectedIfasBodyRemovalSearchText || "");
  }, [selectedIfasBodyRemovalSearchText]);

  useEffect(() => {
    setBodyFilterStatus(selectedIfasBodyRemovalStatus || "");
  }, [selectedIfasBodyRemovalStatus]);

  if (showHistory) {
    return (
      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        <History
          onBack={handleBackFromHistory}
          historyData={historyData}
          isLoading={isLoading}
          historyType="case"
        />
      </Box>
    );
  }

  return (
    <OperationsPageShell
      eyebrow={pageEyebrow}
      title={title}
      description={pageDescription}
      actions={
        <Box display="flex" gap={1} flexWrap="wrap">
          {isIfasCasePortal && canManageOperationalLocations && (
            <Button
              variant="outlined"
              onClick={() => setShowLocationSetup(true)}
              sx={standardGhostBtnSx}
            >
              Operational Locations
            </Button>
          )}
          {isIfasCasePortal && canManagePlannerBranches && (
            <Button
              variant="outlined"
              onClick={() => setShowPlannerSetup(true)}
              sx={standardGhostBtnSx}
            >
              Planner Mortuary Setup
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshOutlinedIcon />}
            onClick={reloadList}
            sx={standardGhostBtnSx}
          >
            Refresh
          </Button>
        </Box>
      }
      meta={
        <>
          <Chip
            label={`${effectiveTotalElements || 0} records`}
            size="small"
            sx={{ bgcolor: ifasColors.softOrange, color: "primary.main", fontWeight: 800 }}
          />
          {driverOnly && isBodyRemoval && (
            <Chip
              label="Assigned to you"
              size="small"
              sx={{ bgcolor: STANDARD_TONES.green.bg, color: STANDARD_TONES.green.color, fontWeight: 800 }}
            />
          )}
        </>
      }
    >
      {isLoading ? <Loader isLoading={isLoading} /> : null}

      {isIfasCasePortal && !isBodyRemoval && !isQuotation && !driverOnly && (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #E5E7EB",
            borderRadius: "14px",
            p: { xs: 2, md: 3 },
            mb: 3,
            backgroundColor: "#fff",
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <FilterAltOutlinedIcon sx={{ color: "#111827" }} />
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#111827" }}>
              Table Filters
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "minmax(260px, 1fr) 300px 300px" },
              gap: 2,
              alignItems: "end",
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 12, color: "#6B7280", mb: 1 }}>
                SEARCH (ALL FIELDS)
              </Typography>
              <TextField
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                placeholder="Search..."
                fullWidth
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: "#9CA3AF", mr: 1 }} />,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#fff",
                  },
                }}
              />
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 12, color: "#6B7280", mb: 1 }}>
                MORTUARY
              </Typography>
              <FormControl fullWidth>
                <Select
                  displayEmpty
                  value={filterBranch}
                  onChange={(e) => setFilterBranch(String(e.target.value || ""))}
                  sx={{ borderRadius: "12px" }}
                  renderValue={(value) => value ? String(value) : "All mortuaries"}
                >
                  <MenuItem value="">All</MenuItem>
                  {branchOptions.map((branch) => (
                    <MenuItem value={branch} key={branch}>{branch}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 12, color: "#6B7280", mb: 1 }}>
                STATUS
              </Typography>
              <FormControl fullWidth>
                <Select
                  displayEmpty
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(String(e.target.value || ""))}
                  sx={{ borderRadius: "12px" }}
                  renderValue={(val) => (val ? String(val) : "Select")}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="DRAFT">DRAFT</MenuItem>
                  <MenuItem value="IN_PROGRESS">IN_PROGRESS</MenuItem>
                  <MenuItem value="COMPLETED">COMPLETED</MenuItem>
                  <MenuItem value="CANCELLED">CANCELLED</MenuItem>
                  <MenuItem value="CLAIM_DECLINED">CLAIM_DECLINED</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Box display="flex" gap={2} mt={3}>
            <Box sx={{ width: 140 }}>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                onClick={() => {
                  setCurrentPage?.(0);
                  applyIfasCaseFilters?.({
                    status: filterStatus || null,
                    searchText: filterSearch,
                    branchName: filterBranch || null,
                    page: 0,
                  });
                }}
              >
                Apply
              </Button>
            </Box>
            <Box sx={{ width: 140 }}>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setFilterSearch("");
                  setFilterStatus("");
                  setFilterBranch("");
                  setCurrentPage?.(0);
                  applyIfasCaseFilters?.({ status: null, searchText: "", branchName: null, page: 0 });
                }}
              >
                Clear
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {isIfasBodyRemovalPortal && isBodyRemoval && !isQuotation && (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #E5E7EB",
            borderRadius: "14px",
            p: { xs: 2, md: 3 },
            mb: 3,
            backgroundColor: "#fff",
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <FilterAltOutlinedIcon sx={{ color: "#111827" }} />
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#111827" }}>
              Table Filters
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 360px" },
              gap: 2,
              alignItems: "end",
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 12, color: "#6B7280", mb: 1 }}>
                SEARCH (ALL FIELDS)
              </Typography>
              <TextField
                value={bodyFilterSearch}
                onChange={(e) => setBodyFilterSearch(e.target.value)}
                placeholder="Search..."
                fullWidth
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: "#9CA3AF", mr: 1 }} />,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#fff",
                  },
                }}
              />
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 12, color: "#6B7280", mb: 1 }}>
                STATUS
              </Typography>
              <FormControl fullWidth>
                <Select
                  displayEmpty
                  value={bodyFilterStatus}
                  onChange={(e) => setBodyFilterStatus(String(e.target.value || ""))}
                  sx={{ borderRadius: "12px" }}
                  renderValue={(val) => (val ? String(val) : "Select")}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="DRAFT">DRAFT</MenuItem>
                  <MenuItem value="ALLOCATED">ALLOCATED</MenuItem>
                  <MenuItem value="DRIVER_ACCEPTED">DRIVER_ACCEPTED</MenuItem>
                  <MenuItem value="EN_ROUTE">EN_ROUTE</MenuItem>
                  <MenuItem value="ARRIVED_AT_SCENE">ARRIVED_AT_SCENE</MenuItem>
                  <MenuItem value="DETAILS_CONFIRMED">DETAILS_CONFIRMED</MenuItem>
                  <MenuItem value="REMOVAL_ORDER_CREATED">REMOVAL_ORDER_CREATED</MenuItem>
                  <MenuItem value="BODY_COLLECTED">BODY_COLLECTED</MenuItem>
                  <MenuItem value="ARRIVED_AT_MORTUARY">ARRIVED_AT_MORTUARY</MenuItem>
                  <MenuItem value="BODY_DELIVERED">BODY_DELIVERED</MenuItem>
                  <MenuItem value="RECEIVED_BY_MORTUARY">RECEIVED_BY_MORTUARY</MenuItem>
                  <MenuItem value="BODY_ASSESSED">BODY_ASSESSED</MenuItem>
                  <MenuItem value="DEATH_CERTIFICATE_ISSUED">DEATH_CERTIFICATE_ISSUED</MenuItem>
                  <MenuItem value="CANCELLED">CANCELLED</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Box display="flex" gap={2} mt={3}>
            <Box sx={{ width: 140 }}>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                onClick={() => {
                  setCurrentPage?.(0);
                  applyIfasBodyRemovalFilters?.({
                    status: bodyFilterStatus || null,
                    searchText: bodyFilterSearch,
                    page: 0,
                  });
                }}
              >
                Apply
              </Button>
            </Box>
            <Box sx={{ width: 140 }}>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setBodyFilterSearch("");
                  setBodyFilterStatus("");
                  setCurrentPage?.(0);
                  applyIfasBodyRemovalFilters?.({ status: null, searchText: "", page: 0 });
                }}
              >
                Clear
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {isQuotation && (
        <Paper
          elevation={0}
          sx={{
            p: 2.25,
            mb: 3,
            border: `1px solid ${ifasColors.border}`,
            borderRadius: "16px",
            background: `linear-gradient(135deg, ${ifasColors.softOrange}, ${ifasColors.surface} 46%)`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              alignItems: { md: "center" },
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  bgcolor: ifasColors.softOrange,
                  color: ifasColors.orange,
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                <FilterAltOutlinedIcon />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 19, fontWeight: 900, color: ifasColors.text }}>
                  Filter quotations
                </Typography>
                <Typography
                  sx={{ mt: 0.4, color: ifasColors.muted, fontSize: 12.5, lineHeight: 1.45, maxWidth: 520 }}
                >
                  Search by quotation ref, funeral file, claimant or deceased name, then narrow by status.
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1 }}>
              <TextField
                size="small"
                placeholder="Search ref, funeral file, claimant, deceased"
                value={quotationFilterSearch}
                onChange={(event) => setQuotationFilterSearch(event.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: ifasColors.muted }} />,
                }}
                sx={{
                  minWidth: 280,
                  bgcolor: ifasColors.surface,
                  "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                }}
              />
              <TextField
                size="small"
                select
                value={quotationFilterStatus}
                onChange={(event) => setQuotationFilterStatus(event.target.value)}
                SelectProps={{ displayEmpty: true }}
                sx={{
                  minWidth: 190,
                  bgcolor: ifasColors.surface,
                  "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                }}
              >
                <MenuItem value="">All statuses</MenuItem>
                {QUOTATION_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {formatOperationsStatus(option)}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
        </Paper>
      )}

      <Grid container spacing={2}>
        {metrics.map((metric, index) => (
          <Grid
            key={index}
            size={{
              xs: 12,
              sm: 6,
              md: isBodyRemoval || isQuotation ? 3 : 4,
            }}
          >
            <StandardMetricCard
              label={metric.label}
              value={metric.value}
              tone={(metric as any).tone || "orange"}
              caption={(metric as any).caption}
              icon={<Box component="img" src={metric.icon} alt="" sx={{ width: 18, height: 18 }} />}
            />
          </Grid>
        ))}
      </Grid>

      {/* Table Section */}
      <Box
        mt={2.75}
        flex={1}
        overflow="auto"
        sx={{
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        <OperationsSectionHeader
          title={recentFilter}
          meta={`${effectiveTotalElements || 0} total`}
        />

        <Paper sx={operationsTablePaperSx} elevation={0}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              px: 2.5,
              py: 2.25,
              borderBottom: `1px solid ${ifasColors.border}`,
            }}
          >
            <Typography sx={{ fontSize: 17, fontWeight: 800, color: ifasColors.text }}>
              {title}
            </Typography>
            <StandardCountBadge count={effectiveTotalElements || 0} />
          </Box>
          <Box sx={standardListScrollSx}>
            <Table sx={{ width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={standardTableHeadCellSx}>
                    {isBodyRemoval
                      ? "REMOVAL REF NO"
                      : isQuotation
                        ? "QUOTATION REF NO"
                        : "CASE REF NO"}
                  </TableCell>
                  <TableCell sx={standardTableHeadCellSx}>
                    {isBodyRemoval
                      ? "CASE REF NO"
                      : isQuotation
                        ? "CLAIMANT NAME"
                        : "INFORMANT NAME"}
                  </TableCell>
                  <TableCell sx={standardTableHeadCellSx}>Deceased Name</TableCell>
                  {isQuotation && (
                    <>
                      <TableCell sx={standardTableHeadCellSx} align="right">
                        Amount
                      </TableCell>
                    </>
                  )}
                  {!(isBodyRemoval || isQuotation) && (
                    <>
                      <TableCell sx={standardTableHeadCellSx}>Mortuary</TableCell>
                      <TableCell sx={standardTableHeadCellSx}>Date of Death</TableCell>
                      <TableCell sx={standardTableHeadCellSx}>Last Updated</TableCell>
                    </>
                  )}
                  {!isQuotation && <TableCell sx={standardTableHeadCellSx}>Assigned</TableCell>}
                  <TableCell sx={standardTableHeadCellSx}>Status</TableCell>
                  {!isQuotation && <TableCell sx={standardTableHeadCellSx}>Actions</TableCell>}
                  {isQuotation && <TableCell sx={standardTableHeadCellSx} align="right" />}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleData.map((item: any) => {
                  const status = isBodyRemoval
                    ? item.bodyRemovalStatus
                    : isQuotation
                      ? item.quotationStatus
                      : item.status;

                  return (
                    <TableRow
                      key={item.id || item.bodyRemovalNumber || item.quotationNumber || item.caseNumber}
                      hover
                      sx={standardTableRowSx}
                      onClick={() => handleOpenDetails(item)}
                    >
                      <TableCell>
                        <Box display="flex" flexDirection="column" gap={0.75}>
                          <Typography component="span">
                            {isBodyRemoval
                          ? item.bodyRemovalNumber
                          : isQuotation ? item.quotationNumber : item.caseNumber || "N/A"}
                          </Typography>
                          {!isBodyRemoval && !isQuotation && (
                            <Chip
                              size="small"
                              label={
                                (item.funeralPackageSourceType || item.funeral_package_source_type) === "POLICY"
                                  ? "Policy Holder"
                                  : "Cash Client"
                              }
                              sx={{
                                alignSelf: "flex-start",
                                height: 24,
                                fontSize: 11,
                                fontWeight: 800,
                                bgcolor:
                                  (item.funeralPackageSourceType || item.funeral_package_source_type) === "POLICY"
                                    ? "#EEF2FF"
                                    : "#FFF4EB",
                                color:
                                  (item.funeralPackageSourceType || item.funeral_package_source_type) === "POLICY"
                                    ? "#3730A3"
                                    : "#C85A00",
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {isBodyRemoval
                          ? item.caseNumber
                          : isQuotation
                            ? `${item.claimantFirstName} ${item.claimantLastName}`
                            : `${item.informantFirstName} ${item.informantLastName}` ||
                              "N/A"}
                      </TableCell>

                      <TableCell>
                        {`${item.deceasedFirstName} ${item.deceasedLastName}` ||
                          "N/A"}
                      </TableCell>

                      {showDateOfDeath && (
                        <>
                          <TableCell>{item.mortuaryName || item.branchName || "N/A"}</TableCell>
                          <TableCell>
                            {item.dateOfDeath
                              ? dayjs(item.dateOfDeath).format("YYYY-MM-DD")
                              : "N/A"}
                          </TableCell>
                          <TableCell>
                            {item.updatedAt
                              ? dayjs(item.updatedAt).format("YYYY-MM-DD")
                              : "N/A"}
                          </TableCell>
                        </>
                      )}

                      {/* <TableCell>
                      {item.bodyRemovalRequired ? "Body Removal" : "Normal"}
                    </TableCell> */}
                      {isQuotation && (
                        <TableCell align="right">{formatR(item.totalAmount)}</TableCell>
                      )}

                      {!isQuotation && (
                        <TableCell>
                          {item.assignedTo ||
                            item.assigned_to ||
                            item.assignedUserName ||
                            item.assignedUser ||
                            (isBodyRemoval ? item.driverName : "") ||
                            "Unassigned"}
                        </TableCell>
                      )}

                      <TableCell>
                        {isBodyRemoval && isBodyRemovalStatusPill(status) ? (
                          <Box
                            px={1}
                            py={0.5}
                            borderRadius="20px"
                            fontWeight={600}
                            display="inline-block"
                            textTransform="none"
                            sx={getBodyRemovalStatusStyles(status)}
                          >
                            {getBodyRemovalStatusLabel(status)}
                          </Box>
                        ) : (
                          <StatusDotChip
                            status={status}
                            tone={isQuotation ? quotationStatusTone(status) : standardStatusTone(status)}
                          />
                        )}
                      </TableCell>

                      {isQuotation ? (
                        <TableCell align="right">
                          <Button
                            variant="outlined"
                            sx={{ ...standardOutlineBtnSx, minHeight: 34, px: 1.6 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenDetails(item);
                            }}
                          >
                            Open
                          </Button>
                        </TableCell>
                      ) : (
                        <TableCell>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedActionItem(item);
                              if (isBodyRemoval) {
                                useBodyRemovalsStore
                                  .getState()
                                  .setSelectedBodyRemoval(item);
                              } else {
                                setSelectedCase(item);
                              }
                              setAnchorEl(e.currentTarget);
                            }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
                {visibleData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={emptyStateColSpan} align="center">
                      {driverOnly
                        ? "No assigned body removal records found."
                        : "No records found."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
          {!isQuotation && (
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => {
                setAnchorEl(null);
                setSelectedActionItem(null);
              }}
            >
              <MenuItem onClick={handleHistoryClick}>History</MenuItem>
              {!isBodyRemoval && canAssignSomeone && (
                <MenuItem onClick={handleAssignSomeoneClick}>Assign Someone</MenuItem>
              )}
            </Menu>
          )}

          {!driverOnly && Boolean(rowsPerPage) && (
            <StandardPager
              page={currentPage}
              total={effectiveTotalElements || 0}
              pageSize={rowsPerPage}
              onPage={setCurrentPage}
            />
          )}
        </Paper>
      </Box>
      <PlannerBranchSetupDialog
        open={showPlannerSetup}
        onClose={() => setShowPlannerSetup(false)}
      />
      <OperationalLocationsDialog
        open={showLocationSetup}
        onClose={() => setShowLocationSetup(false)}
        onChanged={fetchBranchNames}
      />
      <AssignPlannerDialog
        open={showAssignPlanner}
        caseItem={selectedActionItem}
        onClose={() => {
          setShowAssignPlanner(false);
          setSelectedActionItem(null);
        }}
        onAssigned={reloadList}
      />
    </OperationsPageShell>
  );
};

export default CasePortal;
